import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ModalService } from '@indice/ng-components';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  mergeMap,
  of,
  switchMap,
  tap
} from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { GeolocationService } from 'src/app/core/services/geolocation.service';
import { LoanCalculatorValues, LoanService, LoanValues } from 'src/app/services/loan.service';
import { NationalityService } from 'src/app/services/nationality.service';
import {
  AuctionDetails,
  PortalApiService,
  PropertyDetails,
  PropertySummary
} from 'src/app/services/portal-api.service';
import { ToasterService, ToastType } from 'src/app/services/toaster.service';
import { IMortgageLoanData, loanTypes, WorkflowApiService } from 'src/app/services/workflow.service';
import { DropdownItem } from 'src/app/shared/components/dropdown-nationality/dropdown-nationality.component';
import { PropertyCardItemComponent } from 'src/app/shared/components/property-card-item/property-card-item.component';
import { CustomTooltipDirective } from 'src/app/shared/directives/custom-tooltip.directive';
import { mapInstanceProperties } from 'src/app/shared/utilities/mapInstanceProperties';
import {
  FilterDropdownPropertyItem,
  FilterPropertyDropdownComponent
} from "../../shared/components/filter-property-dropdown/filter-property-dropdown.component";
import { DoughnutChartComponent } from "../../shared/components/loan-calulator/doughnut-chart/doughnut-chart.component";
import { LoanCalulatorComponent } from "../../shared/components/loan-calulator/loan-calulator.component";
import { NavigationButtonsComponent } from "../../shared/components/navigation-buttons/navigation-buttons.component";
import { ProgressStepperComponent } from "../../shared/components/progress-stepper/progress-stepper.component";
import { OnboardingExitModalComponent } from '../onboarding/onboarding-exit-modal/onboarding-exit-modal.component';
import { ILoanApplicationModel } from './models';
import { LanguageRouterLinkDirective } from 'src/app/shared/directives/language-router-link.directive';
import { HeaderService } from 'src/app/services/header.service';

enum nationalityType {
  greek = 'greek',
  inEU = 'inEU',
  outsideEU = 'outsideEU',
}

enum propertyFormCategories {
  favorite,
  byCode
}

@Component({
  selector: 'app-loan-application-wizard',
  standalone: true,
  imports: [CommonModule, ProgressStepperComponent, NavigationButtonsComponent, TranslocoDirective, TranslocoPipe, ReactiveFormsModule,
    LoanCalulatorComponent, DoughnutChartComponent, FilterPropertyDropdownComponent, NgxMaskDirective, PropertyCardItemComponent, CustomTooltipDirective, LanguageRouterLinkDirective],
  templateUrl: './loan-application-wizard.component.html',
  styleUrl: './loan-application-wizard.component.css',
  providers: [provideNgxMask()],
})
export class LoanApplicationWizardComponent {
  public countryCodesAndCitizens: DropdownItem[] = [];
  nationalityType = nationalityType;
  public showResultPage: boolean = false;
  public propertyCardSummaryData: PropertySummary | undefined;
  protected propertyId: string | undefined = this._route.snapshot.queryParams["property"];
  protected rennovationCosts: number | undefined = this._route.snapshot.queryParams["rennovationCosts"];
  protected loanAmount: number | undefined = this._route.snapshot.queryParams["loanAmount"];
  protected loanDuration: number | undefined = this._route.snapshot.queryParams["loanDuration"];
  protected loanFixedDuration: number | undefined = this._route.snapshot.queryParams["loanFixedDuration"];
  protected showChoosePropertyUi = !this.propertyId;
  protected stepsMap: { [key: string]: string } = this.showChoosePropertyUi ? {
    'property': 'loan-wizard.steps.property',
    'loan': 'loan-wizard.steps.loan',
    'details': 'loan-wizard.steps.details',
    'financialDetails': 'loan-wizard.steps.finance',
    'review': 'loan-wizard.steps.review',
  } : {
    //values will be replaced by translation
    // 'property': '1. THE PROPERTY',
    'loan': 'loan-wizard.steps.loan',
    'details': 'loan-wizard.steps.details',
    'financialDetails': 'loan-wizard.steps.finance',
    'review': 'loan-wizard.steps.review',
  };
  protected loanForm: FormGroup;
  protected stepKeys: string[] = Object.keys(this.stepsMap);
  protected stepValues: string[] = [];
  protected activeStep: string = this.stepKeys[this.showChoosePropertyUi ? 0 : 1];
  protected propertySummary: PropertyDetails | undefined;
  protected propertyFavourites: PropertyDetails[] | undefined;
  protected propertyRecentlyViewed: PropertyDetails[] | undefined;
  protected searchControl: FormControl = new FormControl();
  protected searchProperty: FormControl = new FormControl();
  protected showBranchList: boolean = false;
  protected isInteractingWithDiv: boolean = false;
  protected coords: { Latitude?: number, Longitude?: number } = {};
  protected currentYear: number = new Date().getFullYear();
  protected hiddenSections: boolean[] = Array(5).fill(false);
  protected loanFormData: ILoanApplicationModel | any;
  protected loanCaseTypeCode: string = 'mortgage-loan';
  protected propertyMortgageCategoryId = '9EEC3373-4411-44C1-82F9-8B12A3C30566'; //this is the category id as provided by nbg
  protected propertyRenovationCategoryId = '300951CE-9CC4-4904-8921-540AD2B6CC20'; //this is the category id as provided by nbg
  protected pagination: { page: number, size: number };
  protected selectedPropertyFromCategory: propertyFormCategories = propertyFormCategories.favorite;
  protected readonly propertyFromCategories = propertyFormCategories;
  protected propertyFavouritesFiltered: FilterDropdownPropertyItem[] = [];
  protected propertySub$: BehaviorSubject<PropertyDetails | undefined> = new BehaviorSubject<PropertyDetails | undefined>(undefined);
  protected propertyByCodeInput = new FormControl('', [Validators.minLength(8)]);
  protected caseId?: string | undefined;
  protected calcValue: LoanCalculatorValues | undefined;
  protected favoritePropertySelected: PropertyDetails | undefined;
  activeLang = 'el';

  constructor(
    private _modalService: ModalService,
    private _translocoService: TranslocoService,
    private _fb: FormBuilder,
    private _geolocationService: GeolocationService,
    private _portalService: PortalApiService,
    private _authService: AuthService,
    private _loanService: LoanService,
    private _route: ActivatedRoute,
    private _nationality: NationalityService,
    private _workflowService: WorkflowApiService,
    private _headerService: HeaderService,
    @Inject(ToasterService) private _toastService: ToasterService,
  ) {
    this.pagination = { page: 1, size: 10 }
    _translocoService.selectTranslateObject('loan-wizard').pipe(
      tap(t => {
        this.applyTranslations(t);
      })
    ).subscribe();
    _translocoService.langChanges$.subscribe(lang => {
      this.activeLang = lang;
    })
    this._portalService.getMySellingProperties(undefined).pipe(
      mergeMap(() => {
        return this._portalService.getMyFavorites(1, 100).pipe(
          catchError((error) => {
            console.error('Error receiving favorites:', error);
            return of(null);
          })
        )
      }),
      tap(favorites => {
        if (favorites) {
          favorites.items?.map(property => {
            this.propertyFavouritesFiltered?.push({
              key: property.id,
              title: property.title,
              address: this.concatPropertyAddress(property),
              floor: property.floor,
              bedrooms: property.numberOfRooms,
              bathrooms: property.numberOfBathrooms,
              constructionYear: property.constructionYear,
              defaultPhoto: property.defaultPhoto?.photoItems?.[0]?.uri ?? property.defaultImagePath
            } as FilterDropdownPropertyItem)
          })
        }
      })
    ).subscribe();

    this._nationality.getCountryCodesAndCitizens().map((x, index) => {
      this.countryCodesAndCitizens.push(
        {
          key: index,
          icon: x.code,
          label: x.citizen,
        } as DropdownItem
      )
    });

    this.loanForm = this._fb.group({
      property: this._fb.group({
        propertyId: ['', Validators.required],
        address: ['', Validators.required],
      }),
      loan: this._fb.group({
        categoryId: ['', Validators.required],
        realEstateValue: ['', Validators.required],
        amount: ['', Validators.required],
        duration: ['', Validators.required],
        fixedInterestDuration: ['', Validators.required],
      }),
      details: this._fb.group({
        tinNumber: ['', Validators.required],
        yearBorn: ['', [Validators.required, this.validYearValidator()]],
        professionalStatus: ['', Validators.required],
        marriageStatus: ['', Validators.required],
        children: ['', Validators.required],
        nationality: ['', Validators.required],
      }),
      financialDetails: this._fb.group({
        grossSalaryPerYear: ['', Validators.required],
        householdDebt: ['', Validators.required],
      }),
      review: this._fb.group({
        consentPersonalDetails: [false, Validators.requiredTrue],
        consentEditApplication: [false, Validators.requiredTrue],
      }),
    });

    this.propertySub$.pipe(
      filter(f => {
        return this.propertySummary?.id !== f?.id
      }),
      tap((t: PropertyDetails | undefined) => {
        this.propertySummary = t;

        this.propertyCardSummaryData = mapInstanceProperties(this.propertySummary as PropertyDetails, PropertySummary);

        this.loanForm.get('property.propertyId')?.setValue(t?.id);
        let addressOrEmpty = '';
        if (t) {
          addressOrEmpty = this.concatPropertyAddress(t);
          this.calcValue = new LoanCalculatorValues(t.price, this.rennovationCosts, this.loanAmount, this.loanDuration, this.loanFixedDuration);
        } else {
          this.calcValue = undefined;
        }
        this.loanForm.get('property.address')?.setValue(addressOrEmpty);
      }),
    ).subscribe();

    if (this.propertyId) {
      this._portalService.getPropertyById(this.propertyId ?? '').pipe(
        catchError((err: any) => {
          // loan-wizard.property-by-code.error.message
          const errorTitle = this._translocoService.translate('loan-wizard.property-by-code.error.title');
          const errorMessage = this._translocoService.translate('loan-wizard.property-by-code.error.message', { message: err.message });
          const unknownMessage = this._translocoService.translate('loan-wizard.property-by-code.unknown.message')
          this._toastService.showBanner(ToastType.Error, errorTitle, errorMessage || unknownMessage, 10000);
          this.propertySub$.next(undefined);

          return EMPTY;
        }),
        tap((t: PropertyDetails) => {
          this.propertySub$.next(t);
        }),
      ).subscribe();
    }

    this._loanService.currentLoanValues.pipe(
      tap((t: LoanValues) => {
        this.loanForm.get('loan.realEstateValue')?.setValue(t.loanCalculatorValues.propertyValue);
        this.loanForm.get('loan.amount')?.setValue(t.loanCalculatorValues.loanAmount);
        this.loanForm.get('loan.duration')?.setValue(t.loanCalculatorValues.loanDuration);
        this.loanForm.get('loan.fixedInterestDuration')?.setValue(t.loanCalculatorValues.loanFixedDuration);
      })
    ).subscribe();

    this._loanService.currentSelectedCategory.pipe(
      tap(t => this.loanForm.get('loan.categoryId')?.setValue(t)),
    ).subscribe();

    this.propertyByCodeInput.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      filter(f => this.propertyByCodeInput.valid),
      switchMap(s => {
        if (!s) {
          return EMPTY;
        }
        this.loanForm.get('property.propertyId')?.setValue(undefined);

        return this._portalService.getPropertyById(s)
      }),
      catchError((err: any) => {
        const errorTitle = this._translocoService.translate('loan-wizard.property-by-code.error.title');
        const errorMessage = this._translocoService.translate('loan-wizard.property-by-code.error.message', { message: err.message });
        this._toastService.showBanner(ToastType.Error, errorTitle, errorMessage, 10000);

        return EMPTY;
      }),
      tap(t => {
        this.favoritePropertySelected = undefined;
        this.propertySub$.next(t)
      })
    ).subscribe();


  }

  onNationalityChange(selectedValue: string) {
    this.loanForm.get('details.nationality')?.setValue(selectedValue);
  }

  public onPropertyFromCategory(category: propertyFormCategories) {
    this.selectedPropertyFromCategory = category;
  }

  handlePropertySelection(property: { id: string; auctionDetails?: AuctionDetails; }) {
    this._portalService.getPropertyById(property.id).pipe(
      catchError((error: any) => {
        this._toastService.showBanner(ToastType.Error, this._translocoService.translate('Failed to get property'), undefined, 10000);

        return EMPTY;
      }),
      tap((t: PropertyDetails) => {
        this.propertyByCodeInput.setValue(null);
        this.favoritePropertySelected = t;
        this.propertySub$.next(t);
      }),
    ).subscribe();
  }

  getStepIndex(key: string): number {
    return Object.keys(this.stepsMap).indexOf(key);
  }

  protected onNextClick(): void {
    this.scrollToTop();
    if (!this.isFormValid()) {
      this.markAllAsTouched(this.loanForm.get(this.activeStep) as FormGroup);

      return;
    }

    const currentIndex = this.stepKeys.indexOf(this.activeStep);
    const nextIndex = this.stepKeys[currentIndex + 1];

    if (this.stepKeys.indexOf(nextIndex) > 0) {
      this.activeStep = this.stepKeys[currentIndex + 1];
      return;
    }

    this.goToResults();
  }

  protected onPrevClick(): void {
    const currentIndex = this.stepKeys.indexOf(this.activeStep);
    if (currentIndex === -1) {
      this.activeStep = this.stepKeys[0];

    } else {
      this.activeStep = this.stepKeys[currentIndex - 1];
    }
  }

  protected hasError(controlName: string): boolean {
    const control = this.loanForm.get(controlName) as FormControl;
    const result = control.invalid && control.touched;
    return result
  }

  protected goToMyRequests(): void {
    throw new Error('Method not implemented.');
  }

  protected openModalComponent(): void {
    const exitModal = this._modalService.show(OnboardingExitModalComponent, {
      animated: true,
      keyboard: true,
      class: 'h-dvh md:h-auto',
    });
    exitModal.onHidden?.subscribe((response: any) => {

    });
  }

  private scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private isFormValid(): boolean {
    const subForm = this.loanForm.get(this.activeStep) as FormGroup;
    const result = subForm === null || subForm.valid;

    return result;
  }

  private applyTranslations(t: any) {
    this.stepKeys.forEach((key, index) => {
      if (t?.stesps && t?.steps[key]) {
        this.stepsMap[key] = `${index + 1}.${t?.steps[key]}`;
      }
    });
    this.stepValues = Object.values(this.stepsMap);
  }

  private goToResults(): void {
    const body = this.constructLoanBody();
    this._workflowService.initiateMortgageLoan(body).pipe(
      catchError((err: any) => {
        this._toastService.showBanner(ToastType.Error, 'Failed to create Request', err?.errors?.InitiateWorkflow.join(', ') || 'Unknown error', 5000);
        return EMPTY;
      }),
      tap((caseId: string) => {
        this.caseId = caseId
        this.showResultPage = true;
        // # Disable "Cancel Progress" validation when closing the wizard on the final step (eg checkout/summary)
        this._headerService.showCancelProgress(false);
      })
    ).subscribe();
  }

  private constructLoanBody(): IMortgageLoanData {
    const loanCategoryId = this.loanForm.get('loan.categoryId')?.value;
    let loanType: string;
    if (loanCategoryId === this.propertyRenovationCategoryId) {
      loanType = loanTypes[loanTypes.renovaction];
    } else {
      loanType = loanTypes[loanTypes.mortgage];
    }

    let rs: IMortgageLoanData = {
      propertyId: this.loanForm.get('property.propertyId')?.value,
      loanType: loanType,
      currency: 'EUR',
      propertyValue: Number(this.loanForm.get('loan.realEstateValue')?.value),
      amount: Number(this.loanForm.get('loan.amount')?.value),
      durationInYears: Number(this.loanForm.get('loan.duration')?.value),
      fixedInterestRateDurationInYears: Number(this.loanForm.get('loan.fixedInterestDuration')?.value),
      tinNumber: this.loanForm.get('details.tinNumber')?.value,
      yearOfBirth: Number(this.loanForm.get('details.yearBorn')?.value),
      professionalStatus: this.loanForm.get('details.professionalStatus')?.value,
      marriageStatus: this.loanForm.get('details.marriageStatus')?.value,
      children: this.loanForm.get('details.children')?.value,
      nationality: this.loanForm.get('details.nationality')?.value,
      yearlyGrossSalary: Number(this.loanForm.get('financialDetails.grossSalaryPerYear')?.value),
      householdDebt: Number(this.loanForm.get('financialDetails.householdDebt')?.value),
    };

    return rs;
  }

  private markAllAsTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markAllAsTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }

  private validYearValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const year = control.value;

      if (year >= 1900 && year <= this.currentYear - 16) {
        return null; // Valid year
      } else {
        return { invalidYear: true }; // Invalid year
      }
    };
  }

  private concatPropertyAddress(propertyDetails: PropertyDetails): string {
    const components = [
      `${propertyDetails.address?.streetName} ${propertyDetails.address?.streetNumber}`,
      propertyDetails.address?.administrativeAreas,
      propertyDetails.address?.prefecture,
    ].filter(f => f); //filter removes empties

    return components.join(', ');
  }
}
